import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { countries } from '../../../data/home/home';

const GlobalPresence: React.FC = () => {
  const [hoveredCountry, setHoveredCountry] = useState<string | null>(null);
  const mapRef = useRef<HTMLDivElement>(null);
  const [flagScreenPos, setFlagScreenPos] = useState<{x: number, y: number} | null>(null);

  // Calculate the screen position of the flag image when hovered
  useEffect(() => {
    if (!hoveredCountry) {
      setFlagScreenPos(null);
      return;
    }
    const country = countries.find(c => c.id === hoveredCountry);
    if (!country || !mapRef.current) return;
    const mapRect = mapRef.current.getBoundingClientRect();
    // Calculate flag center in screen coordinates
    const x = mapRect.left + (country.imgX / 100) * mapRect.width;
    const y = mapRect.top + (country.imgY / 100) * mapRect.height;
    setFlagScreenPos({ x, y });
  }, [hoveredCountry]);

  return (
    <section id="global-presence-section" className="relative w-full pt-16 flex flex-col items-center justify-center bg-white dark:bg-black transition-colors duration-500"
    >
      <div className="max-w-6xl w-full mx-auto text-center mb-12 px-4">
        <span className="inline-block mb-3 px-4 py-1 rounded-full border border-[#FE6019] text-[#FE6019] bg-[#FE6019]/10 font-semibold text-xs md:text-sm tracking-wide mb-6">
          GLOBAL PRESENCE
        </span>
        <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-6">
          Our Global Footprint
        </h2>
        <p className="text-gray-600 dark:text-gray-300 max-w-2xl mx-auto text-base md:text-lg">
        Operating across multiple continents, we bring a full-stack ecosystem of technology, strategy, and execution — empowering businesses to grow across borders.
        </p>
      </div>

      {/* Full-width map container */}
      <div className="relative w-screen overflow-x-visible px-0">
        <div className="relative w-full" style={{ aspectRatio: '2/1', overflow: 'visible' }} ref={mapRef}>
          {/* World Map Background Image */}
          <img
            src="/images/home/global.png"
            alt="World Map"
            className="absolute inset-0 w-full h-full object-contain select-none pointer-events-none"
            draggable={false}
          />

          {/* SVG overlay*/}
          <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 2 }}>
            {countries.map((country) => {
              return (
                <g key={country.id}>
                  <line
                    x={`${country.imgX}%`}
                    y={`${country.imgY}%`}
                    stroke="#FE6019"
                    strokeWidth="1.2"
                  />
                </g>
              );
            })}
          </svg>

          {/* Country Markers (flag images) */}
          {countries.map((country) => {
            // Popup positioning logic for this marker
            let popupPosition: React.CSSProperties = {
              left: '110%', // default: right of marker
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 30,
              position: 'absolute',
              whiteSpace: 'normal',
            };
            // If marker is near the right edge, show popup to the left
            if (country.imgX > 80) {
              popupPosition = {
                right: '110%',
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 30,
                position: 'absolute',
                whiteSpace: 'normal',
              };
            }
            return (
              <div
                key={country.id}
                className="absolute transform -translate-x-1/2 -translate-y-1/2 group"
                style={{
                  left: `${country.imgX}%`,
                  top: `${country.imgY}%`,
                  zIndex: 3,
                }}
              >
                {/* Country Marker */}
                <motion.div
                  className="relative cursor-pointer"
                  onMouseEnter={() => setHoveredCountry(country.id)}
                  onMouseLeave={() => setHoveredCountry(null)}
                  whileHover={{ scale: 1.3 }}
                  whileTap={{ scale: 0.9 }}
                >
                  {/* Pulse Animation */}
                  <div className="absolute inset-0 rounded-full bg-[#FE6019]/30 animate-ping"></div>
                  {/* Flag Image in Circle (smaller) */}
                  <div className="relative w-8 h-8 rounded-full border-2 border-[#FE6019] shadow-lg overflow-hidden bg-white dark:bg-gray-800 flex items-center justify-center">
                    <img
                      src={country.flagImg}
                      alt={country.id + ' flag'}
                      className="w-7 h-7 object-cover rounded-full"
                      draggable={false}
                    />
                  </div>
                  {/* Popup: only show if this marker is hovered */}
                  {hoveredCountry === country.id && (
                    <div
                      style={popupPosition}
                      className="min-w-[180px] max-w-xs p-4 rounded-lg shadow-xl border border-[#FE6019] bg-white dark:bg-neutral-900 text-gray-900 dark:text-white font-primary text-sm transition-colors duration-300"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <img src={country.flagImg} alt={country.id + ' flag'} className="w-6 h-6 rounded-full border border-[#FE6019]" />
                        <span className="font-bold text-base" style={{ color: '#FE6019', fontFamily: 'Merriweather, serif' }}>{country.info.title}</span>
                      </div>
                      <div className="mb-2 text-xs text-gray-700 dark:text-gray-300" style={{ fontFamily: 'Poppins, sans-serif' }}>{country.info.description}</div>
                      {country.info.stats && (
                        <ul className="list-disc pl-5 space-y-1 text-xs text-gray-800 dark:text-gray-200" style={{ fontFamily: 'Poppins, sans-serif' }}>
                          {country.info.stats.map((stat, idx) => (
                            <li key={idx} className="text-[#FE6019]">{stat}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </motion.div>
              </div>
            );
          })}
        </div>
      </div>

    </section>
  );
};

export default GlobalPresence; 